import React, { useState, useEffect } from 'react';
import type { Video } from '../services/videoService';
import videoService from '../services/videoService';

interface VideoModalProps {
  video: Video | null;
  onClose: () => void;
  onEdit?: (video: Video) => void;
  onDelete?: (video: Video) => void;
}

const VideoModal: React.FC<VideoModalProps> = ({ video, onClose, onEdit, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [loadingPlayback, setLoadingPlayback] = useState(false);
  const [playbackError, setPlaybackError] = useState<string | null>(null);

  // Fetch playback URL when video changes
  useEffect(() => {
    if (video && video.status === 'ready') {
      setLoadingPlayback(true);
      setPlaybackError(null);
      setPlaybackUrl(null);

      videoService
        .getPlaybackUrl(video.id)
        .then((data) => {
          setPlaybackUrl(data.playback_url);
          setLoadingPlayback(false);
        })
        .catch((error) => {
          console.error('Failed to get playback URL:', error);
          setPlaybackError('Failed to load video playback');
          setLoadingPlayback(false);
        });
    }
  }, [video]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (video) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [video, onClose]);

  if (!video) return null;

  // Format duration
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '--:--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(video);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 flex-1 pr-4 truncate">
              {video.name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Video Preview / Player */}
            <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden relative">
              {loadingPlayback ? (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <p className="text-sm">Loading video...</p>
                  </div>
                </div>
              ) : playbackError ? (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm">{playbackError}</p>
                    {video.thumbnail_full_url || video.thumbnail_preview_url ? (
                      <img
                        src={video.thumbnail_full_url || video.thumbnail_preview_url}
                        alt={video.name}
                        className="mt-4 max-w-md mx-auto rounded opacity-50"
                      />
                    ) : null}
                  </div>
                </div>
              ) : playbackUrl && video.status === 'ready' ? (
                <video
                  controls
                  className="w-full h-full object-contain"
                  poster={video.thumbnail_full_url || video.thumbnail_preview_url}
                  preload="metadata"
                >
                  <source src={playbackUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : video.thumbnail_full_url || video.thumbnail_preview_url ? (
                <img
                  src={video.thumbnail_full_url || video.thumbnail_preview_url}
                  alt={video.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Description */}
            {video.description && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{video.description}</p>
              </div>
            )}

            {/* Tags */}
            {video.tags && video.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {video.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Video Information */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Video Information</h3>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">Duration</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {formatDuration(video.duration)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Resolution</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {video.resolution_width && video.resolution_height
                      ? `${video.resolution_width}x${video.resolution_height}`
                      : 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Frame Rate</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {video.frame_rate ? `${video.frame_rate} fps` : 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Bitrate</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {video.bitrate ? `${video.bitrate} kbps` : 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Format</dt>
                  <dd className="text-sm font-medium text-gray-900 uppercase">
                    {video.format || 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">File Size</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {formatFileSize(video.file_size)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Video Codec</dt>
                  <dd className="text-sm font-medium text-gray-900 uppercase">
                    {video.codec_video || 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Audio Codec</dt>
                  <dd className="text-sm font-medium text-gray-900 uppercase">
                    {video.codec_audio || 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Source Type</dt>
                  <dd className="text-sm font-medium text-gray-900 capitalize">
                    {video.source_type}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Status</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    <span
                      className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                        video.status === 'ready'
                          ? 'bg-green-100 text-green-800'
                          : video.status === 'processing'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {video.status}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Created</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {formatDate(video.created_at)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Updated</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {formatDate(video.updated_at)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Remote URL (if applicable) */}
            {video.source_type === 'remote' && video.remote_url && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Remote URL</h3>
                <div className="bg-gray-50 rounded-lg p-3 break-all text-sm text-gray-700">
                  {video.remote_url}
                </div>
              </div>
            )}

            {/* Storage Key */}
            {video.storage_key && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Storage Key</h3>
                <div className="bg-gray-50 rounded-lg p-3 break-all text-sm text-gray-700 font-mono">
                  {video.storage_key}
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              {onEdit && (
                <button
                  onClick={() => onEdit(video)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              )}
              {onDelete && !showDeleteConfirm && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              )}
              {showDeleteConfirm && (
                <>
                  <span className="text-sm text-gray-700">Are you sure?</span>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Confirm Delete
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
